/**
 * FaxWizard
 *
 * A multi-step wizard for sending faxes with either uploaded blobs or document instances.
 * The wizard guides users through: selecting attachments, entering a recipient number,
 * and reviewing before sending. It supports partial success with toasts, basic error handling,
 * and progress tracking. Consumers can optionally provide document instances to switch the
 * first step into Letter of Representation (LoR) selection mode.
 *
 * Steps:
 * 1. Documents (blobs or document instances)
 * 2. Recipient
 * 3. Review
 *
 * Example:
 * ```tsx
 * <FaxWizard
 *   matterId={matterId}
 *   onSuccess={() => refetchTransmissions()}
 * />
 * ```
 */
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  AttachmentSelectionStep,
  Wizard,
  type WizardStepDefinition,
} from '@/components/postgrid/wizard';
import { LorSelectionStep } from '@/components/postgrid/wizard/LorSelectionStep';
import { useGetBlobsQuery } from '@/services/api/blobService';
import {
  SendFaxFromBlobRequest,
  SendMergedFaxRequest,
  useSendFaxFromBlobMutation,
  useSendFaxFromDocumentInstanceMutation,
  useSendMergedFaxMutation,
} from '@/services/api/faxService';

import { FaxRecipientStep } from './FaxRecipientStep';
import { FaxReviewStep } from './FaxReviewStep';
// Future: import { DocumentInstanceSelectionStep } ... when using document instances

/**
 * Wizard form data managed across steps.
 */
type WizardInitialData = {
  recipient_number?: string;
  attachment_blob_ids?: string[];
  attachment_document_ids?: string[];
  // Optional fax cover sheet fields
  cover_page_to?: string;
  cover_page_from?: string;
  cover_page_message?: string;
};

/**
 * Props for FaxWizard.
 *
 * - `matterId`: The matter to associate fax transmissions with.
 * - `onSuccess`: Callback fired when all send operations complete (even if partially successful).
 * - `onCancel`: Optional callback when the wizard is cancelled.
 * - `isLoading`: External loading state that disables wizard interactions.
 * - `initialData`: Initial form values for the wizard steps.
 * - `documentItems`: Optional list of document instances; when provided, the first step becomes LoR selection.
 */
interface FaxWizardProps {
  matterId: string;
  onSuccess: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  initialData?: WizardInitialData;
  // Optional: pass document instances to switch to document mode
  documentItems?: Array<{ id: string; title: string; createdAt?: string }>;
}

const faxWizardSteps: WizardStepDefinition[] = [
  {
    id: 'attachments',
    title: 'Documents',
    description: 'Select documents to fax',
  },
  {
    id: 'recipient',
    title: 'Recipient',
    description: 'Enter fax number',
  },
  {
    id: 'review',
    title: 'Review',
    description: 'Confirm & send',
  },
];

const lorFaxWizardSteps: WizardStepDefinition[] = [
  {
    id: 'lor-selection',
    title: 'LoR',
    description: 'Select Letter of Representation',
  },
  {
    id: 'additional-attachments',
    title: 'Attachments',
    description: 'Add additional documents',
  },
  {
    id: 'recipient',
    title: 'Recipient',
    description: 'Enter fax number',
  },
  {
    id: 'review',
    title: 'Review',
    description: 'Confirm & send',
  },
];

/**
 * A three-step wizard to send one or more documents via fax with feedback.
 */
export const FaxWizard: React.FC<FaxWizardProps> = ({
  matterId,
  onSuccess,
  onCancel,
  isLoading = false,
  initialData = {},
  documentItems,
}) => {
  const [sendFaxFromBlob, { isLoading: isSending }] =
    useSendFaxFromBlobMutation();
  const [sendFaxFromDocument] = useSendFaxFromDocumentInstanceMutation();
  const [sendMergedFax, { isLoading: isMerging }] =
    useSendMergedFaxMutation();
  const { data: blobs } = useGetBlobsQuery(matterId);
  const isLorMode = Boolean(documentItems && documentItems.length > 0);
  const [, setSendProgress] = useState<{ sent: number; total: number } | null>(
    null,
  );

  const handleMergedFaxComplete = useCallback(
    async (wizardData: WizardInitialData) => {
      const blobIds: string[] = wizardData.attachment_blob_ids || [];
      const docIds: string[] = wizardData.attachment_document_ids || [];
      const recipientNumber = wizardData.recipient_number || '';
      const coverTo = (wizardData.cover_page_to || '').trim();
      const coverFrom = (wizardData.cover_page_from || '').trim();
      const coverMessage = (wizardData.cover_page_message || '').trim();

      if (!docIds.length) {
        throw new Error('No Letter of Representation selected');
      }
      if (!recipientNumber) {
        throw new Error('No recipient number provided');
      }

      const totalDocs = docIds.length + blobIds.length;

      const payload: SendMergedFaxRequest = {
        recipient_number: recipientNumber,
        matter_id: matterId,
        document_instance_ids: docIds,
        blob_ids: blobIds.length > 0 ? blobIds : undefined,
        request_type: 'general_document',
        entity_name: 'Letter of Representation',
        document_type: 'LoR with Attachments',
        cover_message:
          coverMessage || `Letter of Representation for matter ${matterId}`,
        ...(coverFrom ? { sender_name: coverFrom } : {}),
        ...(coverTo ? { cover_page_to: coverTo } : {}),
        ...(coverFrom ? { cover_page_from: coverFrom } : {}),
        additional_metadata: {
          ...(coverTo ? { cover_page_to: coverTo } : {}),
          ...(coverFrom ? { cover_page_from: coverFrom } : {}),
          ...(coverMessage ? { cover_page_message: coverMessage } : {}),
        },
      };

      await sendMergedFax(payload).unwrap();

      toast.success(
        `Fax sent successfully with ${totalDocs} merged document${totalDocs !== 1 ? 's' : ''}! Check fax transmissions for status updates.`,
        { duration: 5000 },
      );

      onSuccess();
    },
    [matterId, onSuccess, sendMergedFax],
  );

  const handleIndividualFaxComplete = useCallback(
    async (wizardData: WizardInitialData) => {
      const blobIds: string[] = wizardData.attachment_blob_ids || [];
      const docIds: string[] = wizardData.attachment_document_ids || [];
      const recipientNumber = wizardData.recipient_number || '';
      const coverTo = (wizardData.cover_page_to || '').trim();
      const coverFrom = (wizardData.cover_page_from || '').trim();
      const coverMessage = (wizardData.cover_page_message || '').trim();

      if (!blobIds.length && !docIds.length) {
        throw new Error('No documents selected');
      }
      if (!recipientNumber) {
        throw new Error('No recipient number provided');
      }

      setSendProgress({ sent: 0, total: blobIds.length + docIds.length });

      const selectedBlobs =
        blobs?.filter((blob) => blobIds.includes(blob.id)) || [];

      const blobResults = await Promise.allSettled(
        blobIds.map(async (blobId: string, index: number) => {
          const blob = selectedBlobs.find((b) => b.id === blobId);
          const fileName =
            blob?.original_filename || `Document_${index + 1}.pdf`;

          const payload: SendFaxFromBlobRequest = {
            recipient_number: recipientNumber,
            blob_id: blobId,
            request_type: 'general_document',
            matter_id: matterId,
            entity_name: 'Document Fax',
            document_type: 'General Document',
            file_name: fileName,
            cover_message:
              coverMessage ||
              `Fax transmission of ${fileName} for matter ${matterId}`,
            ...(coverFrom ? { sender_name: coverFrom } : {}),
            ...(coverTo || coverFrom || coverMessage
              ? {
                  additional_metadata: {
                    ...(coverTo ? { cover_page_to: coverTo } : {}),
                    ...(coverFrom ? { cover_page_from: coverFrom } : {}),
                    ...(coverMessage
                      ? { cover_page_message: coverMessage }
                      : {}),
                  },
                }
              : {}),
          };

          const result = await sendFaxFromBlob(payload).unwrap();
          setSendProgress((prev) =>
            prev ? { ...prev, sent: prev.sent + 1 } : null,
          );
          return result;
        }),
      );

      const docResults = await Promise.allSettled(
        docIds.map(async (docId: string) => {
          const result = await sendFaxFromDocument({
            recipient_number: recipientNumber,
            matter_id: matterId,
            document_instance_id: docId,
            cover_message: coverMessage || `LoR for matter ${matterId}`,
            ...(coverFrom ? { sender_name: coverFrom } : {}),
            ...(coverTo || coverFrom || coverMessage
              ? {
                  additional_metadata: {
                    ...(coverTo ? { cover_page_to: coverTo } : {}),
                    ...(coverFrom ? { cover_page_from: coverFrom } : {}),
                    ...(coverMessage
                      ? { cover_page_message: coverMessage }
                      : {}),
                  },
                }
              : {}),
          }).unwrap();
          setSendProgress((prev) =>
            prev ? { ...prev, sent: prev.sent + 1 } : null,
          );
          return result;
        }),
      );

      const results = [...blobResults, ...docResults];
      const failures = results.filter((r) => r.status === 'rejected');
      const successes = results.filter((r) => r.status === 'fulfilled');

      if (failures.length > 0 && successes.length === 0) {
        throw new Error(`All ${failures.length} fax(es) failed to send`);
      } else if (failures.length > 0) {
        toast.warning(
          `${successes.length} of ${blobIds.length + docIds.length} document(s) sent successfully. ${failures.length} failed.`,
          { duration: 5000 },
        );
      } else {
        toast.success(
          `${blobIds.length + docIds.length} document${blobIds.length + docIds.length !== 1 ? 's' : ''} sent successfully! Check fax transmissions for status updates.`,
          { duration: 5000 },
        );
      }

      setSendProgress(null);
      onSuccess();
    },
    [blobs, matterId, onSuccess, sendFaxFromBlob, sendFaxFromDocument],
  );

  const handleComplete = useCallback(
    async (wizardData: WizardInitialData) => {
      try {
        if (isLorMode) {
          await handleMergedFaxComplete(wizardData);
        } else {
          await handleIndividualFaxComplete(wizardData);
        }
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error('Failed to send fax:', error);
        setSendProgress(null);
        const e = error as
          | { status?: number; message?: string }
          | Error
          | Record<string, any>
          | undefined;
        let status: number | undefined;
        let message: string | undefined;

        if (
          e &&
          typeof e === 'object' &&
          'status' in e &&
          typeof (e as any).status === 'number'
        ) {
          status = (e as any).status as number;
        }
        if (e && typeof e === 'object') {
          if ('message' in e && typeof (e as any).message === 'string') {
            message = (e as any).message as string;
          } else if (
            'data' in e &&
            (e as any).data &&
            typeof (e as any).data.message === 'string'
          ) {
            message = (e as any).data.message as string;
          }
        } else if (typeof error === 'string') {
          message = error;
        }

        let errorMessage = 'Failed to send fax. Please try again.';
        if (typeof status === 'number' && status >= 400 && status < 500) {
          errorMessage =
            'Failed to send fax. Please check the phone number and documents, then try again.';
        } else if (typeof status === 'number' && status >= 500) {
          errorMessage =
            'Fax service is temporarily unavailable. Please try again later.';
        } else if (!status && message) {
          errorMessage = message;
        } else if (!status) {
          errorMessage =
            'Unable to connect to the server. Please check your connection.';
        }

        toast.error(errorMessage);
      }
    },
    [isLorMode, handleMergedFaxComplete, handleIndividualFaxComplete],
  );

  const steps = isLorMode ? lorFaxWizardSteps : faxWizardSteps;

  const wizardChildren: React.ReactElement[] = isLorMode
    ? [
        <LorSelectionStep
          key="lor"
          matterId={matterId}
          documents={documentItems!}
        />,
        <AttachmentSelectionStep
          key="attachments"
          matterId={matterId}
          optional
          title="Additional Attachments"
          description="Optionally attach additional documents (e.g. police report) to include with the LoR fax."
        />,
        <FaxRecipientStep key="recipient" />,
        <FaxReviewStep key="review" matterId={matterId} />,
      ]
    : [
        <AttachmentSelectionStep key="attachments" matterId={matterId} />,
        <FaxRecipientStep key="recipient" />,
        <FaxReviewStep key="review" matterId={matterId} />,
      ];

  return (
    <Wizard
      title="Send Fax"
      description="Follow these steps to select documents and send them via fax."
      steps={steps}
      onComplete={handleComplete}
      onCancel={onCancel}
      isLoading={isLoading || isSending || isMerging}
      initialData={initialData}
    >
      {wizardChildren}
    </Wizard>
  );
};
